/**
 * alunoBuilder — sub-container do slice de Aluno (core CRUD + helpers básicos).
 *
 * Escopo: Aluno + Raça + CEP + Foto. Cobre as views ListAlunos/CreateAluno/EditAluno
 * e composables de formulário. Tabs do perfil (documentos/protocolos/notas/etc) vão
 * em `perfilAlunoBuilder`.
 *
 * Extraído de `container.ts` na Sprint F.5-polish-extras-4.
 *
 * @since F.5-polish-extras-4
 */
import type { $Fetch } from 'ofetch';

import { HttpAlunoGateway } from '@/infrastructure/http/HttpAlunoGateway';
import { HttpRacaGateway } from '@/infrastructure/http/HttpRacaGateway';
import { ViaCepGateway } from '@/infrastructure/http/ViaCepGateway';

import {
  CreateAlunoUseCaseImpl, GetAlunoUseCaseImpl,
  ListAlunosUseCaseImpl, UpdateAlunoUseCaseImpl,
} from '@/application/usecase/AlunoUseCaseImpl';
import { ListRacasUseCaseImpl } from '@/application/usecase/RacaUseCaseImpl';
import { LookupCepUseCaseImpl } from '@/application/usecase/CepUseCaseImpl';
import { UploadAlunoFoto } from '@/application/usecase/UploadAlunoFoto';
import { DownloadAlunoFoto } from '@/application/usecase/DownloadAlunoFoto';
import { VerificarContatoAluno } from '@/application/usecase/VerificarContatoAluno';

import type { AlunoGateway } from '@/application/port/out/AlunoGateway';
import type { RacaGateway } from '@/application/port/out/RacaGateway';
import type { CepGateway } from '@/application/port/out/CepGateway';

export function buildAlunoSlice(httpClient: $Fetch) {
  const alunoGateway: AlunoGateway = new HttpAlunoGateway(httpClient);
  const racaGateway: RacaGateway = new HttpRacaGateway(httpClient);
  const cepGateway: CepGateway = new ViaCepGateway();

  const useCases = {
    listAlunos: new ListAlunosUseCaseImpl(alunoGateway),
    getAluno: new GetAlunoUseCaseImpl(alunoGateway),
    createAluno: new CreateAlunoUseCaseImpl(alunoGateway),
    updateAluno: new UpdateAlunoUseCaseImpl(alunoGateway),
    listRacas: new ListRacasUseCaseImpl(racaGateway),
    lookupCep: new LookupCepUseCaseImpl(cepGateway),
    uploadAlunoFoto: new UploadAlunoFoto(alunoGateway),
    downloadAlunoFoto: new DownloadAlunoFoto(alunoGateway),
    verificarContatoAluno: new VerificarContatoAluno(alunoGateway),
  };

  const gateways = { alunoGateway, racaGateway, cepGateway };

  return { useCases, gateways };
}
